import { lazy, Suspense, useEffect } from 'react';
import { Routes, Route } from 'react-router-dom';
import { DevPanel } from '@/dev/DevPanel';
import { installMetaSfx } from '@/meta/metaSfx';
import { HomeScreen } from '@/screens/HomeScreen';
import { PlayScreen } from '@/screens/PlayScreen';
import { MapScreen } from '@/screens/MapScreen';
import { LoginScreen } from '@/screens/LoginScreen';
import { AuthCallbackScreen } from '@/screens/AuthCallbackScreen';
import { SharedLevelScreen } from '@/screens/SharedLevelScreen';
import { WildcardScreen } from '@/screens/WildcardScreen';
import { NotFoundScreen } from '@/screens/NotFoundScreen';
import { ProfileScreen } from '@/screens/ProfileScreen';
import { SettingsScreen } from '@/screens/SettingsScreen';
import { CollectionScreen } from '@/screens/CollectionScreen';
import { AchievementsScreen } from '@/screens/AchievementsScreen';
import { WorkshopScreen } from '@/screens/WorkshopScreen';
import { CatalogScreen } from '@/screens/CatalogScreen';

// только дев-сборка: конструктор уровней. Ленивый динамический импорт — весь чанк
// (редактор + рендер) не эмитится в прод-бандл (ветка гейта сворачивается в false).
const ConstructorScreen = lazy(() =>
  import('@/dev/constructor/ConstructorScreen').then((m) => ({ default: m.ConstructorScreen })),
);

export function App() {
  // Звук интерфейса — ОДНИМ делегатом на документ, а не обработчиком на каждой кнопке:
  // кнопок в мете больше сотни в десяти экранах, общего компонента `<Button>` нет, а
  // класс `.btn` требует от них UI-кит. Значит делегат покрывает и те экраны, которых
  // ещё нет, и его нельзя забыть подключить в новом.
  useEffect(() => installMetaSfx(), []);
  return (
    <>
      <Routes>
        <Route path="/" element={<HomeScreen />} />
        <Route path="/map" element={<MapScreen />} />
        <Route path="/play/:levelId" element={<PlayScreen />} />
        <Route path="/login" element={<LoginScreen />} />
        <Route path="/auth/callback" element={<AuthCallbackScreen />} />
        <Route path="/profile" element={<ProfileScreen />} />
        <Route path="/settings" element={<SettingsScreen />} />
        <Route path="/collection" element={<CollectionScreen />} />
        <Route path="/achievements" element={<AchievementsScreen />} />
        <Route path="/workshop" element={<WorkshopScreen />} />
        <Route path="/catalog" element={<CatalogScreen />} />
        <Route path="/p/:slug" element={<SharedLevelScreen />} />
        {/* только дев-сборка: wildcard-уровень для теста механики по ссылке.
            Константа инлайнится Vite → без VITE_DEV_TOOLS=1 роут и модуль экрана
            выпиливаются три-шейкингом (как DevPanel). Бэкенд-эндпоинт тоже дев-онли. */}
        {import.meta.env.VITE_DEV_TOOLS === '1' && (
          <Route path="/wildcard/:slug" element={<WildcardScreen />} />
        )}
        {/* уровень из конструктора по ссылке «Поделиться» — тот же дев-харнесс, другой источник */}
        {import.meta.env.VITE_DEV_TOOLS === '1' && (
          <Route path="/shared/:slug" element={<WildcardScreen source="shared" />} />
        )}
        {import.meta.env.VITE_DEV_TOOLS === '1' && (
          <Route
            path="/dev/constructor"
            element={<Suspense fallback={<div style={{ padding: 24 }}>Загрузка конструктора…</div>}><ConstructorScreen /></Suspense>}
          />
        )}
        <Route path="*" element={<NotFoundScreen />} />
      </Routes>
      {/* только дев-сборка: константа инлайнится Vite; без VITE_DEV_TOOLS=1
          ветка мертва и модуль панели целиком выпадает из бандла (tree-shake) */}
      {/* Дев-панель отключена (решение автора 2026-08-04): её кнопка «🛠» висела
          поверх HUD и лезла в кадр демо-уровня, встроенного в лендинг. Роуты
          /wildcard и /dev/constructor под VITE_DEV_TOOLS остаются — убрана
          только плавающая кнопка. Вернуть = снять `false &&`. */}
      {false && import.meta.env.VITE_DEV_TOOLS === '1' && <DevPanel enabled />}
    </>
  );
}
